import { isFunction, isObject, performanceNow } from './utils';
import { TIMER_TYPE } from '../enums';
const hasWindow = typeof window !== 'undefined';
const requestIC = (hasWindow && window.requestIdleCallback) || function (handler) {
  const startTime = Date.now();
  return setTimeout(() => {
    handler({
      didTimeout: false,
      timeRemaining() {
        return Math.max(0, 50.0 - (Date.now() - startTime));
      }
    });
  }, 1000);
};

const cancelIC = (hasWindow && window.cancelIdleCallback) || function (id) {
  clearTimeout(id);
};

// @ts-ignore
const cancelAnimationFrame = hasWindow && (window.cancelAnimationFrame || window.mozCancelAnimationFrame);
interface TaskItem {
  delay: number;
  count?: number;
  backgroundTask?: boolean;
  taskID: number;
  loopCount: number;
  intervalID: number | null;
  timeoutID: number | null;
  rafID: number | null;
  ricID: number | null;
  taskName: string;
  worker?: Worker;
  onVisibilitychange?: () => void;
  callback: () => void;
}
/**
 * 定时器，功能：
 * 1. 支持定时执行回调 [1,n] 次，用于常规延迟、定时操作
 * @example
 * // 默认嵌套执行，count=0 无限次
 * timer.run(callback, {delay: 2000});
 * // count=1 等同于 原始setTimeout
 * timer.run(callback, {delay: 2000, count:0});
 * 2. 支持 RAF 执行回调，用于小流渲染，audio音量获取等任务，需要渲染频率稳定，支持页面退后台后，用 setTimeout 接管，最短 1s 执行一次
 * @example
 * // 默认60fps，可以根据单位时长换算，默认开启后台执行
 * timer.run('raf', callback, {fps: 60});
 * // 设置执行次数
 * timer.run('raf', callback, {fps: 60, count: 300, backgroundTask: false});
 * 3. 支持空闲任务执行回调, requestIdleCallback 在帧渲染的空闲时间执行任务，可以用于 storage 写入等低优先级的任务
 * @example
 * // 默认执行 1 次 callback
 * timer.run('ric', callback);
 * // 如果没有空闲，超过 delay 时长，callback 将被推入执行队列
 * timer.run('ric', callback, {delay:5000});
 * // 循环执行低优先级任务， 默认 1s 20次，取决于实际运行情况
 * timer.run('ric', callback, {delay: 5000, count: 0});
 * @example
 * // 支持原生setInterval 但不推荐使用，定时任务推荐用 timeout
 * timer.run('interval', callback, {delay:2000, count:10})
 */

class Timer {
  static taskMap = new Map<number, TaskItem>();
  static currentTaskID = 1;
  static generateTaskID() {
    return this.currentTaskID++;
  }
  private static sharedWorker: Worker | null = null;
  private static workerTasks = new Map<number, TaskItem>();
  /**
   *
   * @param {string} taskName 'interval' 'timeout' 'ric' 'raf'
   * @param {function} callback
   * @param {object} options include:
   * @param {number} options.delay millisecond
   * @param {number} options.fps fps 和 delay 二选一，delay 优先，fps 其次。
   * @param {number} options.count 定时器回调执行次数，0 无限次 or n次
   * @param {boolean} options.backgroundTask 在页面静默后是否继续执行定时器
   */
  static run(taskName: TIMER_TYPE, callback: any, options?: { delay?: number, count?: number, backgroundTask?: boolean, fps?: number; }) {
    // 把 fps 换算成 delay
    if (options?.fps) options.delay = options.delay || Number((1000 / options.fps).toFixed(2));
    // default options
    if (taskName === TIMER_TYPE.INTERVAL) {
      options = { ...{ delay: 2000, count: 0, backgroundTask: true }, ...options };
    } else if (taskName === TIMER_TYPE.RIC) {
      options = { ...{ delay: 10000, count: 0 }, ...options };
    } else if (taskName === TIMER_TYPE.RAF) {
      options = { ...{ fps: 60, delay: 16.6, count: 0, backgroundTask: true }, ...options };
    } else {
      options = { ...{ delay: 2000, count: 0, backgroundTask: true }, ...options };
    }
    // 1. 创建 taskID，作为 timer task 的唯一标识，在本函数执行完后返回，用于在调用的地方实现互斥逻辑
    // 2. 根据 taskName 执行相应的函数
    const taskItem = {
      taskID: this.generateTaskID(),
      loopCount: 0,
      intervalID: null,
      timeoutID: null,
      rafID: null,
      ricID: null,
      taskName,
      callback,
      ...options,
      delay: options.delay!
    };
    this.taskMap.set(taskItem.taskID, taskItem);
    // console.log(`timer run task:${taskItem.taskName}, task queue size: ${this.taskMap.size}`);
    // 分发任务
    // @ts-ignore
    this[taskName](taskItem);
    return taskItem.taskID;
  }
  /**
   * 定时循环执行回调函数
   * 可以指定循环的时间间隔
   * 可以指定循环次数
   * @param {object} taskItem
   * @returns ID
   */
  static interval(taskItem: TaskItem) {
    // setInterval 缺点，浏览器退后台会降频，循环执行间隔时间不可靠
    // 创建进入定时器循环的任务函数,函数内：1. 判断是否满足执行条件，2.执行 callback
    // 通过 setInterval 执行任务函数
    // 将 intervalID 记录到 taskMap 对应的 item
    const task = () => {
      taskItem.callback();
      taskItem.loopCount += 1;
      if (Timer.isBreakLoop(taskItem)) {
        return;
      }
    };
    return taskItem.intervalID = setInterval(task, taskItem.delay);
  }
  static intervalInWorker(taskItem: TaskItem) {
    if (!Timer.sharedWorker) {
      Timer.sharedWorker = new Worker(URL.createObjectURL(new Blob([`
        const timers = new Map();
        self.onmessage = function(e) {
          const { taskId, delay, type } = e.data;
          if (type === 'start') {
            timers.set(taskId, setInterval(() => {
              self.postMessage({ type: 'tick', taskId });
            }, delay));
          } else if (type === 'stop') {
            clearInterval(timers.get(taskId));
            timers.delete(taskId);
          }
        };
      `], { type: 'application/javascript' })));

      Timer.sharedWorker.onmessage = (e: MessageEvent<{ type: string, taskId: number; }>) => {
        if (e.data.type === 'tick') {
          const task = Timer.workerTasks.get(e.data.taskId);
          if (task) {
            if (Timer.isBreakLoop(task)) {
              Timer.sharedWorker?.postMessage({
                type: 'stop',
                taskId: task.taskID
              });
              Timer.workerTasks.delete(task.taskID);
            } else {
              task.callback();
              task.loopCount += 1;
            }
          }
        }
      };
    }
    Timer.workerTasks.set(taskItem.taskID, taskItem);

    Timer.sharedWorker.postMessage({
      taskId: taskItem.taskID,
      delay: taskItem.delay,
      type: 'start'
    });
  }
  /**
   * 延迟执行回调
   * count = 0,循环
   * count = n, 执行n次
   * @param {object} taskItem
   *
   */
  static timeout(taskItem: TaskItem) {
    // setTimeout 浏览器退后台，延迟变为至少1s
    const task = () => {
      // 执行回调
      taskItem.callback();
      taskItem.loopCount += 1;
      if (Timer.isBreakLoop(taskItem)) {
        return;
      }
      // 不修正延迟，每次callback间隔平均
      return taskItem.timeoutID = setTimeout(task, taskItem.delay);
    };
    return taskItem.timeoutID = setTimeout(task, taskItem.delay);
  }
  static ric(taskItem: TaskItem) {
    // 不支持 requestIdleCallback 的环境，用 setTimeout 模拟实现
    let last = performanceNow();
    let delta;
    const task = () => {
      // console.log('ric task');
      delta = performanceNow() - last;
      if (delta >= taskItem.delay) {
        // 减去callback耗时，修正当前起始时间
        last = performanceNow() - Math.floor(delta % taskItem.delay);
        taskItem.callback();
        taskItem.loopCount += 1;
      }
      if (Timer.isBreakLoop(taskItem)) {
        return;
      }
      return taskItem.ricID = requestIC(task, { timeout: taskItem.delay });
    };
    return taskItem.ricID = requestIC(task, { timeout: taskItem.delay });
  }
  /**
   * 支持设定执行频率，默认情况下RAF 根据显示器刷新率执行，比如60hz，一秒执行60次，16.6ms, 如果要渲染30fps 的画面，需要设置 delay 1000/30 = 33.33ms
   * 注意：RAF 与 CSS Animation 同时使用时会频繁触发样式重计算，导致CPU占用过高
   * @param {*} taskItem
   */
  static raf(taskItem: TaskItem) {
    // 不支持 requestAnimationFrame，以及页面退后台的情况，用 setTimeout 模拟实现
    let last = performanceNow();
    let delta;
    const task = () => {
      // console.log('task', delta, last);
      if (document.hidden && taskItem.backgroundTask) {
        delta = performanceNow() - last;
        last = performanceNow();
        taskItem.callback();
        taskItem.loopCount += 1;
        if (Timer.isBreakLoop(taskItem)) {
          return;
        }
        // console.log('delta', delta, taskItem.delay, Math.floor(delta % taskItem.delay));
        // 在谷歌浏览器中，setTimeout在浏览器不可见状态下间隔低于1s的会变为1s，大于等于1s的会变成N+1s的间隔值。火狐浏览器下setTimeout的最小间隔时间会变为1s,大于等于1s的间隔不变。ie浏览器在不可见状态前后的间隔时间不变。
        // delay 减去 callback 耗时，修正下一次delay，类似 setInterval
        return taskItem.timeoutID = setTimeout(task, taskItem.delay - Math.floor(delta % taskItem.delay));
      }
      // 包含上一次callback执行耗时 以及上一次定时器耗时
      delta = performanceNow() - last;
      if (delta >= taskItem.delay) {
        // 减去callback耗时，修正当前起始时间
        last = performanceNow() - Math.floor(delta % taskItem.delay);
        taskItem.callback();
        taskItem.loopCount += 1;
      }
      if (Timer.isBreakLoop(taskItem)) {
        return;
      }
      return taskItem.rafID = requestAnimationFrame(task);
    };
    taskItem.rafID = requestAnimationFrame(task);
    // 退后台后，用 setTimeout 实现
    if (taskItem.backgroundTask) {
      const onVisibilitychange = () => {
        // console.log(document.visibilityState);
        if (document.hidden) {
          // 当 hidden setTimeout 接管 raf的任务，
          const delta = performanceNow() - last;
          if (delta >= taskItem.delay) {
            task();
          } else {
            taskItem.timeoutID = setTimeout(task, taskItem.delay - delta);
          }
        }
      };
      document.addEventListener('visibilitychange', onVisibilitychange);
      taskItem.onVisibilitychange = onVisibilitychange;
      // 页面在 hidden 状态下执行了 RAF，需要触发一次 task
      if (document.hidden) {
        onVisibilitychange();
      }
    }

    return taskItem.taskID;
  }

  static hasTask(taskID: number) {
    return this.taskMap.has(taskID);
  }

  static clearTask(taskID: number) {
    if (!this.taskMap.has(taskID)) {
      return true;
    }
    const { intervalID, timeoutID, rafID, ricID, onVisibilitychange } = this.taskMap.get(taskID)!;
    if (intervalID) {
      clearInterval(intervalID);
    }
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    if (rafID && cancelAnimationFrame) {
      cancelAnimationFrame(rafID);
    }
    if (ricID) {
      cancelIC(ricID);
    }
    if (onVisibilitychange) {
      document.removeEventListener('visibilitychange', onVisibilitychange);
    }
    this.taskMap.delete(taskID);
    // console.log('timer clearTask end  ', `| size:${this.taskMap.size}`);
    return true;
  }
  /**
   * 判断是否需要退出循环
   * 1. 如果已移除出定时队列，退出当前任务
   * 2. 如果当前任务已满足次数限制，则退出当前任务
   * @param {object} taskItem
   * @returns
   */
  static isBreakLoop(taskItem: TaskItem) {
    if (!this.hasTask(taskItem.taskID)) {
      return true;
    }
    if (taskItem.count !== 0 && taskItem.loopCount >= taskItem.count!) {
      this.clearTask(taskItem.taskID);
      return true;
    }
    return false;
  }
}

export default Timer;
